import type { RequestContext } from "../context/request-context.js";
import type {
  InspectionRepository,
  ChecklistRepository,
  RecommendationRepository,
  IntegrityReportRepository
} from "../../repositories/operational-repositories.js";
import {
  type Inspection,
  validateInspectionNature
} from "../../domain/inspection/inspection-domain.js";
import type { Checklist, ChecklistComponent, ChecklistPhoto } from "../../domain/inspection/checklist-domain.js";
import type { Recommendation } from "../../domain/recommendation/recommendation-domain.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
const randomUUID = () => globalThis.crypto.randomUUID();
import { domainId, type EquipmentId, type InspectionId, type ChecklistId, type ChecklistComponentId, type ProfessionalId, type RecommendationId, type IntegrityReportId } from "../../shared/kernel/identifiers.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

import { notificationService } from "./notification-service.js";

/**
 * Chave lógica de identidade de um ChecklistComponent dentro de UM checklist —
 * normaliza acento/caixa/espaço para comparar "Tubo" com "TUBO" ou "tubo ",
 * nunca para ser usada como id. A identidade persistente (a primary key real)
 * é resolvida separadamente a partir dos componentes JÁ EXISTENTES do
 * checklist atual (ver saveChecklistState) — isto é só o critério de
 * correspondência.
 */
function normalizeComponentDefinitionKey(raw: string | null | undefined): string {
  return String(raw ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

export class InspectionService {
  constructor(
    public inspectionRepo: InspectionRepository,
    public checklistRepo: ChecklistRepository,
    public equipmentService?: any,
    public identityRepo?: any,
    public recommendationRepo?: RecommendationRepository,
    public reportRepo?: IntegrityReportRepository
  ) {}

  async startInspection(
    context: RequestContext,
    req: {
      equipmentId: EquipmentId;
      projectId?: string | null;
      unitAreaId?: string | null;
      workOrderId?: string | null;
      subWorkOrderId?: string | null;
      operationalCondition?: string | null;
      inspectionType: string;
      modality: 'INTERNAL' | 'EXTERNAL' | 'GENERAL';
      nature: 'INITIAL' | 'PERIODIC' | 'EXTRAORDINARY';
      extraordinaryReason?: string;
      inspectorId: ProfessionalId;
      notes?: string;
      startDate?: string | null;
      endDate?: string | null;
    }
  ): Promise<{ inspection: Inspection; checklist: Checklist }> {
    const clientId = context.activeClientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.InspectionCreate);

    validateInspectionNature(req.nature, req.extraordinaryReason ?? null);

    // AUDITORIA 2026-09-27 (AUD-006): o Inspetor designado é conferido ANTES
    // de gravar. Antes a conferência rodava depois do save e dentro de um
    // catch que a engolia — nunca bloqueava nada. Professional inexistente ou
    // inativo é recusado; a restrição por TIPO (só "Inspetor") não é aplicada
    // porque o Engenheiro também executa inspeção (CLAUDE.md §45).
    if (req.inspectorId && this.identityRepo) {
      const inspector = await this.identityRepo.getProfessional(req.inspectorId);
      if (!inspector) {
        throw new AppError(ErrorCode.Validation, 'Inspector not found', { safeMessage: 'O Inspetor responsável selecionado não existe no cadastro de Profissionais.' });
      }
      if (inspector.status !== 'ACTIVE') {
        throw new AppError(ErrorCode.Validation, 'Inspector inactive', { safeMessage: 'O Inspetor responsável selecionado está inativo.' });
      }

      // Designar um Inspetor diferente de si mesmo exige permissão de atribuição (Fase 7/9).
      // Quem não a possui (o próprio Inspetor/Técnico) só pode criar inspeção em seu próprio nome.
      if (!context.permissions.has(PermissionCode.ReportAssignInspector) && !context.permissions.has(PermissionCode.ReportReadAll) && context.professionalId) {
        if (String(req.inspectorId) !== String(context.professionalId)) {
          throw new AppError(ErrorCode.Authorization, "Você só possui permissão para criar inspeções sob sua própria responsabilidade técnica.");
        }
      }
    }

    // Snapshot imutável do equipamento: PRÉ-REQUISITO da inspeção. Antes, se a
    // geração falhasse (equipamento inexistente ou sem acesso), a inspeção
    // nascia apontando para um id de snapshot INVENTADO ("snapshot-<uuid>"),
    // só com um console.warn — o RI depois não achava os dados técnicos
    // (AUD-005). Agora a falha chega a quem pediu, com o motivo.
    let equipmentSnapshotId: string | null = null;
    if (this.equipmentService && typeof this.equipmentService.createSnapshot === 'function') {
      const snap = await this.equipmentService.createSnapshot(
        context,
        req.equipmentId,
        `Snapshot imutável gerado na inicialização da inspeção ${req.nature} (${req.inspectionType})`
      );
      equipmentSnapshotId = snap.id;
    }

    const now = new Date().toISOString();
    const inspection: Inspection = {
      id: domainId<InspectionId>(randomUUID()),
      clientId,
      equipmentId: req.equipmentId,
      equipmentSnapshotId,
      projectId: req.projectId ?? null,
      unitAreaId: req.unitAreaId ?? null,
      workOrderId: req.workOrderId ?? null,
      subWorkOrderId: req.subWorkOrderId ?? null,
      operationalCondition: req.operationalCondition ?? null,
      inspectionType: req.inspectionType,
      modality: req.modality,
      nature: req.nature,
      extraordinaryReason: req.extraordinaryReason ?? null,
      startDate: req.startDate || now,
      endDate: req.endDate ?? null,
      inspectorId: req.inspectorId,
      notes: req.notes ?? null,
      status: 'DRAFT',
      version: 1,
      createdAt: now,
      updatedAt: now
    };

    const checklist: Checklist = {
      id: domainId<ChecklistId>(randomUUID()),
      clientId,
      inspectionId: inspection.id,
      version: 1,
      createdAt: now,
      updatedAt: now
    };

    await this.inspectionRepo.save(context, inspection);
    await this.checklistRepo.save(context, checklist);

    if (inspection.inspectorId && this.identityRepo) {
      // Só a NOTIFICAÇÃO é "melhor esforço" — a validação do Inspetor já rodou antes de gravar.
      try {
        const inspector = await this.identityRepo.getProfessional(inspection.inspectorId);
        if (inspector) {
          if (inspector.userId) {
          await notificationService.createNotification({
            userId: inspector.userId,
            type: 'INFO',
            event: 'INSPECTION_ASSIGNED',
            title: 'Nova inspeção atribuída',
            message: `Você foi designado para a inspeção do equipamento ${req.equipmentId}.`,
            entityType: 'Inspection',
            entityId: inspection.id,
            clientId: inspection.clientId,
            ...(inspection.projectId ? { projectId: inspection.projectId } : {}),
            idempotencyKey: `INSPECTION_ASSIGNED_${inspection.id}`
          });
          }
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para Inspetor (Inspeção)', err);
      }
    }

    return { inspection, checklist };
  }

  async saveChecklistComponentResult(
    context: RequestContext,
    checklistId: ChecklistId,
    req: {
      groupId: string;
      definitionComponentId: string;
      result: 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE' | null;
      description?: string;
      inspectionNotes?: string;
    }
  ): Promise<ChecklistComponent> {
    const clientId = context.activeClientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.ChecklistUpdate);

    const checklist = await this.checklistRepo.findById(context, checklistId);
    if (!checklist) {
      throw new AppError(ErrorCode.NotFound, 'Checklist not found');
    }

    const component: ChecklistComponent = {
      id: domainId<ChecklistComponentId>(randomUUID()),
      clientId,
      checklistId,
      groupId: req.groupId,
      definitionComponentId: req.definitionComponentId,
      result: req.result,
      description: req.description ?? null,
      inspectionNotes: req.inspectionNotes ?? null,
      user: context.userId,
      timestamp: new Date().toISOString(),
      photos: []
    };

    if (component.result === 'REJECTED' && (!component.description || component.description.trim().length === 0)) {
      throw new AppError(ErrorCode.Validation, 'Description is required when checklist component is REJECTED');
    }

    await this.checklistRepo.saveComponent(context, component);
    return component;
  }

  async listInspections(context: RequestContext, equipmentId: EquipmentId): Promise<readonly Inspection[]> {
    return this.inspectionRepo.findByEquipment(context, equipmentId);
  }

  async listAllInspections(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly Inspection[]> {
    return this.inspectionRepo.listAll(context, options);
  }

  async getInspectionById(context: RequestContext, id: InspectionId): Promise<Inspection | null> {
    return this.inspectionRepo.findById(context, id);
  }

  async save(context: RequestContext, inspection: Inspection): Promise<Inspection> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.InspectionUpdate);
    await this.inspectionRepo.save(context, inspection);
    return inspection;
  }

  async getChecklistForInspection(
    context: RequestContext,
    inspectionId: InspectionId
  ): Promise<{ checklist: Checklist | null; components: readonly ChecklistComponent[]; photos: readonly any[] }> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.InspectionRead);

    let resolvedInspectionId = inspectionId;
    let checklist = await this.checklistRepo.findByInspection(context, resolvedInspectionId);

    // Auto-heal: If checklist not found, check if inspectionId was actually a reportId
    if (!checklist && this.reportRepo) {
      try {
        const report = await this.reportRepo.findById(context, inspectionId as any);
        if (report && report.inspectionId) {
          resolvedInspectionId = report.inspectionId;
          checklist = await this.checklistRepo.findByInspection(context, resolvedInspectionId);
          if (!checklist) {
            const legacyChecklist = await this.checklistRepo.findByInspection(context, report.id as any);
            if (legacyChecklist) {
              legacyChecklist.inspectionId = resolvedInspectionId;
              legacyChecklist.updatedAt = new Date().toISOString();
              await this.checklistRepo.save(context, legacyChecklist);
              checklist = legacyChecklist;
            }
          }
        }
      } catch {}
    }

    if (!checklist) {
      return { checklist: null, components: [], photos: [] };
    }
    const components = await this.checklistRepo.getComponents(context, checklist.id);
    const photos: any[] = [];
    if (this.checklistRepo.getPhotos) {
      for (const comp of components) {
        try {
          const compPhotos = await this.checklistRepo.getPhotos(context, comp.id);
          photos.push(...compPhotos);
        } catch {}
      }
    }
    return { checklist, components, photos };
  }

  async getChecklistState(
    context: RequestContext,
    inspectionId: InspectionId
  ): Promise<{ checklist: Checklist | null; components: readonly ChecklistComponent[]; photos: readonly any[] }> {
    return this.getChecklistForInspection(context, inspectionId);
  }

  async saveChecklistState(
    context: RequestContext,
    req: {
      inspectionId: InspectionId;
      checklistId?: ChecklistId;
      components: any[];
      photos?: any[];
    }
  ): Promise<{
    checklist: Checklist;
    checklistId: ChecklistId;
    inspectionId: InspectionId;
    componentCount: number;
    photoCount: number;
    components: readonly ChecklistComponent[];
    photos: readonly any[];
  }> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.ChecklistUpdate);

    console.log('[CHECKLIST-SAVE-SERVER]', {
      inspectionIdReceived: req.inspectionId,
      activeClientId: context.activeClientId,
      componentCountReceived: (req.components || []).length
    });

    let inspection = await this.inspectionRepo.findById(context, req.inspectionId);
    let resolvedInspectionId = req.inspectionId;

    // Auto-heal: If inspection not found by id, check if req.inspectionId is actually a reportId
    if (!inspection && this.reportRepo) {
      try {
        const report = await this.reportRepo.findById(context, req.inspectionId as any);
        if (report && report.inspectionId) {
          resolvedInspectionId = report.inspectionId;
          inspection = await this.inspectionRepo.findById(context, resolvedInspectionId);
        }
      } catch {}
    }

    if (!inspection) {
      throw new AppError(ErrorCode.NotFound, 'Inspection not found for checklist state save');
    }

    const clientId = context.activeClientId || inspection.clientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const scopedContext: RequestContext = {
      ...context,
      activeClientId: clientId as any
    };

    let checklist = await this.checklistRepo.findByInspection(scopedContext, resolvedInspectionId);

    // Auto-heal: Check if a checklist was legacy-saved under report ID
    if (!checklist && resolvedInspectionId !== req.inspectionId) {
      const legacyChecklist = await this.checklistRepo.findByInspection(scopedContext, req.inspectionId);
      if (legacyChecklist) {
        legacyChecklist.inspectionId = resolvedInspectionId;
        legacyChecklist.updatedAt = new Date().toISOString();
        await this.checklistRepo.save(scopedContext, legacyChecklist);
        checklist = legacyChecklist;
      }
    }

    const now = new Date().toISOString();
    if (!checklist) {
      checklist = {
        id: req.checklistId || domainId<ChecklistId>(randomUUID()),
        clientId,
        inspectionId: resolvedInspectionId,
        version: 1,
        createdAt: now,
        updatedAt: now
      };
      await this.checklistRepo.save(scopedContext, checklist);
    } else {
      checklist.updatedAt = now;
      await this.checklistRepo.save(scopedContext, checklist);
    }

    console.log('[CHECKLIST-SAVE-RESOLVED]', {
      checklistId: checklist.id,
      checklistInspectionId: checklist.inspectionId,
      checklistClientId: checklist.clientId
    });

    const existingComponents = await this.checklistRepo.getComponents(scopedContext, checklist.id);
    const existingById = new Map(existingComponents.map(c => [String(c.id), c] as const));
    const existingByDefinitionKey = new Map<string, ChecklistComponent>();
    for (const c of existingComponents) {
      const key = normalizeComponentDefinitionKey(c.definitionComponentId);
      // First occurrence wins — a current checklist should not have two
      // components resolving to the same logical definition key.
      if (!existingByDefinitionKey.has(key)) {
        existingByDefinitionKey.set(key, c);
      }
    }
    const incomingComponentIds = new Set<string>();

    for (let idx = 0; idx < (req.components || []).length; idx++) {
      const item = req.components[idx];
      const definitionKey = normalizeComponentDefinitionKey(item.definitionComponentId || item.name || `comp-${idx + 1}`);

      // REGRESSION GUARD:
      // Catalog component IDs (e.g., PP-TUBO) are definition IDs, never global
      // ChecklistComponent primary keys. Persistent IDs must remain isolated per
      // inspection/checklist. See checklist-component-identity-isolation.test.ts.
      //
      // IDENTITY RESOLUTION — never trust item.id (which may be a catalog
      // definition id, e.g. "PP-TUBO", reused across every inspection of the
      // same equipment type) as the persistent primary key. Authority is the
      // CURRENT checklist's own existing components:
      //   1) an exact match of the incoming id INSIDE this checklist, or
      //   2) a component in THIS checklist whose definition key matches.
      // Only when neither exists do we mint a new id, namespaced by the
      // checklist id so the same catalog id in a different inspection/checklist
      // can never collide with this one.
      let resolvedExisting: ChecklistComponent | undefined;
      let identityAction: 'UPDATE_CURRENT' | 'INSERT_NAMESPACED' | 'COLLISION_REKEYED' = 'INSERT_NAMESPACED';

      if (item.id && existingById.has(String(item.id))) {
        resolvedExisting = existingById.get(String(item.id));
        identityAction = 'UPDATE_CURRENT';
      } else if (existingByDefinitionKey.has(definitionKey)) {
        resolvedExisting = existingByDefinitionKey.get(definitionKey);
        identityAction = 'UPDATE_CURRENT';
      }

      let persistentComponentId: string;
      if (resolvedExisting) {
        persistentComponentId = String(resolvedExisting.id);
      } else {
        const safeDefinitionName = definitionKey.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
        persistentComponentId = `comp-${checklist.id}-${safeDefinitionName}`;
        // The namespaced id itself could — in principle — already belong to
        // ANOTHER checklist if two different definition keys normalize to the
        // same slug. Detect that here, before ever calling saveComponent, and
        // re-key deterministically instead of silently stealing the row.
        if (incomingComponentIds.has(persistentComponentId)) {
          persistentComponentId = `comp-${checklist.id}-${safeDefinitionName}-${idx + 1}`;
          identityAction = 'COLLISION_REKEYED';
        }
      }
      incomingComponentIds.add(persistentComponentId);

      console.log('[CHECKLIST-COMPONENT-IDENTITY]', {
        incomingId: item.id ?? null,
        definitionKey,
        existingCurrentId: resolvedExisting ? String(resolvedExisting.id) : null,
        resolvedPersistentId: persistentComponentId,
        currentChecklistId: checklist.id,
        action: identityAction
      });

      const resultVal = item.result || item.status || 'PENDING';
      const validResult = (['APPROVED', 'REJECTED', 'NOT_APPLICABLE', 'CLIENT_DISCRETION', 'PENDING'].includes(resultVal) ? resultVal : 'PENDING') as any;

      const compEntity: ChecklistComponent = {
        id: persistentComponentId as any,
        clientId,
        checklistId: checklist.id,
        groupId: item.category || item.groupId || item.name || 'Componente',
        definitionComponentId: item.name || item.definitionComponentId || 'Componente',
        result: validResult,
        description: item.description || item.condition || (validResult === 'APPROVED' ? 'Sem anomalias (Aprovado)' : (validResult === 'REJECTED' ? (item.technicalComment || 'Anomalia registrada') : null)),
        inspectionNotes: item.inspectionNotes || item.technicalComment || null,
        user: (context.userId || 'USER-1') as any,
        timestamp: item.timestamp || now,
        photos: []
      };

      await this.checklistRepo.saveComponent(scopedContext, compEntity);

      if (this.checklistRepo.clearPhotos) {
        await this.checklistRepo.clearPhotos(scopedContext, persistentComponentId);
      }

      if (item.photos && Array.isArray(item.photos) && this.checklistRepo.savePhoto) {
        for (let pIdx = 0; pIdx < item.photos.length; pIdx++) {
          const ph = item.photos[pIdx];
          const photoUrl = typeof ph === 'string' ? ph : (ph?.url || ph?.fileUrl || ph?.blobId || ph?.image || '');
          const photoComment = typeof ph === 'string' ? '' : (ph?.comment || '');
          if (photoUrl) {
            const dbPhoto: ChecklistPhoto = {
              id: (typeof ph === 'object' && ph?.id) ? ph.id : `photo-${persistentComponentId}-${pIdx + 1}` as any,
              clientId,
              componentId: persistentComponentId as any,
              fileUrl: photoUrl,
              comment: photoComment || null,
              order: (typeof ph === 'object' && ph?.order) ? ph.order : pIdx + 1,
              createdBy: (context.userId || 'USER-1') as any,
              createdAt: now
            };
            await this.checklistRepo.savePhoto(scopedContext, dbPhoto);
          }
        }
      }

      // Persist component recommendations if provided and repository is available
      if (item.recommendations && Array.isArray(item.recommendations) && this.recommendationRepo) {
        let report: any = null;
        if (this.reportRepo) {
          report = await this.reportRepo.findByInspection(scopedContext, resolvedInspectionId);
        }
        for (let rIdx = 0; rIdx < item.recommendations.length; rIdx++) {
          const rec = item.recommendations[rIdx];
          const recScope = rec.scope || rec.description || '';
          if (recScope.trim()) {
            const code = rec.code || rec.recommendationNumber || undefined;
            const recEntity: Recommendation = {
              id: rec.id ? (rec.id as RecommendationId) : domainId<RecommendationId>(randomUUID()),
              code,
              recommendationNumber: code,
              clientId,
              // CHECKLIST-REC-FK-01: integrityReportId é NULL quando RI ainda não existe.
              // O vínculo com RI real ocorre posteriormente via ReportService.createDraftReport().
              // NUNCA usar fallback fictício como `ri-${inspectionId}` pois viola FK.
              integrityReportId: report ? report.id : null,
              inspectionId: resolvedInspectionId,
              equipmentId: inspection.equipmentId,
              componentId: persistentComponentId,
              componentName: item.name,
              checklistItemId: persistentComponentId,
              criticality: (rec.criticality === 'ALTA' || rec.criticality === 'CRÍTICA' || rec.criticality === 'HIGH' || rec.criticality === 'CRITICAL') ? 'HIGH' : (rec.criticality === 'MÉDIA' || rec.criticality === 'MEDIUM') ? 'MODERATE' : 'LOW',
              discipline: rec.discipline || 'CALDEIRARIA',
              executeIn: (rec.execution === 'PARADA' || rec.executeIn === 'SHUTDOWN') ? 'SHUTDOWN' : 'ROUTINE',
              scope: recScope.startsWith(item.name) ? recScope : `${item.name}: ${recScope}`,
              zr: rec.zr || null,
              version: 1,
              createdAt: now,
              updatedAt: now
            };
            await this.recommendationRepo.save(scopedContext, recEntity);
          }
        }
      }
    }

    // Also persist top-level photos if provided in req
    if (req.photos && Array.isArray(req.photos) && this.checklistRepo.savePhoto) {
      for (let pIdx = 0; pIdx < req.photos.length; pIdx++) {
        const ph = req.photos[pIdx];
        const photoUrl = typeof ph === 'string' ? ph : (ph?.url || ph?.fileUrl || ph?.blobId || ph?.image || '');
        const photoComment = typeof ph === 'string' ? '' : (ph?.comment || '');
        const targetComponentId = typeof ph === 'object' ? ph?.componentId : null;
        if (photoUrl && targetComponentId) {
          const dbPhoto: ChecklistPhoto = {
            id: (typeof ph === 'object' && ph?.id) ? ph.id : (`photo-${targetComponentId}-${pIdx + 1}` as any),
            clientId,
            componentId: targetComponentId as any,
            fileUrl: photoUrl,
            comment: photoComment || null,
            order: (typeof ph === 'object' && ph?.order) ? ph.order : pIdx + 1,
            createdBy: (context.userId || 'USER-1') as any,
            createdAt: now
          };
          await this.checklistRepo.savePhoto(scopedContext, dbPhoto);
        }
      }
    }

    // Delete components that were removed from the checklist
    if (existingComponents && existingComponents.length > 0) {
      for (const ec of existingComponents) {
        if (!incomingComponentIds.has(ec.id)) {
          try {
            await this.checklistRepo.deleteComponent(scopedContext, ec.id);
          } catch (errDel) {
            console.warn('Could not delete removed component:', ec.id, errDel);
          }
        }
      }
    }

    // Roundtrip verification: read back from repository
    const readBackComponents = await this.checklistRepo.getComponents(scopedContext, checklist.id);
    const readBackPhotos: any[] = [];
    if (this.checklistRepo.getPhotos) {
      for (const comp of readBackComponents) {
        try {
          const cPhotos = await this.checklistRepo.getPhotos(scopedContext, comp.id);
          readBackPhotos.push(...cPhotos);
        } catch {}
      }
    }

    console.log('[CHECKLIST-SAVE-READBACK]', {
      checklistId: checklist.id,
      inspectionId: checklist.inspectionId,
      persistedComponentCount: readBackComponents.length,
      persistedPhotoCount: readBackPhotos.length
    });

    // Strong integrity assert: never return success on a partial write. If
    // the caller sent N components, the checklist must read back exactly N —
    // anything less means saveComponent silently failed or collided for at
    // least one item, and that must surface as an explicit error, not a
    // quiet success the UI would mistake for "8 saved".
    const expectedCount = (req.components || []).length;
    if (expectedCount > 0 && readBackComponents.length !== expectedCount) {
      throw new AppError(
        ErrorCode.Persistence,
        `Checklist integrity failure: ${expectedCount} components received, ${readBackComponents.length} persisted`,
        { safeMessage: `Falha de integridade ao persistir Checklist: ${expectedCount} componentes recebidos, ${readBackComponents.length} persistidos.` }
      );
    }

    return {
      checklist,
      checklistId: checklist.id,
      inspectionId: checklist.inspectionId,
      componentCount: readBackComponents.length,
      photoCount: readBackPhotos.length,
      components: readBackComponents,
      photos: readBackPhotos
    };
  }

  async deleteInspection(context: RequestContext, id: InspectionId): Promise<void> {
    try {
      await this.inspectionRepo.delete(context, id);
    } catch (e) {
      console.warn("Error deleting inspection from repo:", e);
    }

    if (this.checklistRepo && (this.checklistRepo as any).deleteByInspection) {
      try {
        await (this.checklistRepo as any).deleteByInspection(context, id);
      } catch (e) {
        console.warn("Error cascading checklist deletion:", e);
      }
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(`ri_checklist_insp_${id}`);
        localStorage.removeItem(`ri_performed_tests_${id}`);
        localStorage.removeItem(`ri_ref_docs_${id}`);
      }
    } catch {
      // Ignore storage errors
    }
  }
}
